import {
  Component,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  Input,
  ViewChild,
  ElementRef,
  ViewEncapsulation
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  ContentService,
  ToolsService,
  RafService,
  TrackingService,
} from '@app/services/services';
import {
  FileType,
  FileTypeVal,
  TopicVal,

  Breakpoints, Topic,
} from '@app/shared/enums';
import { DocumentSnapshot } from '@angular/fire/firestore';
import { Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { environment } from '@env/environment';

import debounce from 'lodash.debounce';

const SEARCH_AS_YOU_TYPE_DELAY = 400;
const DEBOUNCE_HEIGHT_DELAY = 200;
const PREVENT_KEYS = {
  Enter: true,
  ArrowLeft: true,
  ArrowRight: true,
  ArrowUp: true,
  ArrowDown: true,
  Home: true,
  End: true,
  PageUp: true,
  PageDown: true,
  Alt: true,
  AltGraph: true,
  CapsLock: true,
  Control: true,
  NumLock: true,
  Shift: true,
  Tab: true,
  ' ': true,
};

// tslint:disable: member-ordering
@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SearchComponent implements OnInit, OnChanges, OnDestroy {
  @Input() topic: string;
  @Input() type: string;
  @Input() filterKeywords: string;

  @ViewChild('searchContainer') searchContainer: ElementRef;
  @ViewChild('suggestionsContainer')
  suggestionsContainer: ElementRef;
  @ViewChild('resultsContainer')
  resultsContainer: ElementRef;
  @ViewChild('resultsHeaderContainer')
  resultsHeaderContainer: ElementRef;

  active = false;
  filterNumber = 0;
  filterIcon = 'normal';
  filtersLevels: {}[];
  filtersTypes: {}[];
  filtersTopics: {}[];
  params = {
    keywords: '',
    filters: {
      topic: '',
      level: undefined,
      type: '',
    },
  };
  pageSize = 12;
  firstResearch = 0;
  isFirstResearch = true;
  results: any[] = [];
  resultsHeading: string;

  // When searching from Firestore DB and the size of the results is higher than the page size, store the next result to load more
  nextResult: DocumentSnapshot<any> = null;
  // When searching from Elastic Search and the size of the results is higher than the page size, store at which index we are to load more
  nextResultIndex = 0;
  isMobile = this.tools.bpIsLowerOrEqualThan(Breakpoints.small);
  isSearching = false;
  popularKeywords: string[] = [];
  searchSuggestions: string[] = [];
  subscriptions: Subscription[] = [];
  topicsPlaceholder: string;
  transitions: any;
  isTopicRevision = false;
  isTypeRevision = false;
  topicOrType: string;
  debounceTime: number = 750;

  constructor(
    private content: ContentService,
    public translate: TranslateService,
    private raf: RafService,
    private tools: ToolsService,
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private tracking: TrackingService,
  ) {
    this.onKeywordsKeyUp = debounce(this.onKeywordsKeyUp, this.debounceTime);

    this.filtersLevels = Object.entries(
      translate.instant('Common.Forms.Levels')
    );
    this.filtersTypes = Object.entries(
      translate.instant('Common.Search.Types')
    );

    this.transitions = {
      filterCount: { target: 0, interpolated: 0, speed: 0.25 },
      loading: { target: 0, interpolated: 0, speed: 0.15 },
      resetBtn: { target: 0, interpolated: 0, speed: 0.15 },
      searchHeight: { target: 0, interpolated: 0, speed: 0.15 },
      searchOpacity: { target: 0, interpolated: 0, speed: 0.2 },
      suggestions: { target: 0, interpolated: 0, speed: 0.15 },
      suggestionsOpacity: { target: 0, interpolated: 0, speed: 0.2 },
      loadingResults: { target: 0, interpolated: 0, speed: 0.15 },
      loadingMore: { target: 0, interpolated: 0, speed: 0.15 },
      results: { target: 0, interpolated: 0, speed: 0.15 },
      resultsHeader: { target: 0, interpolated: 0, speed: 0.15 },
      resultsOpacity: { target: 0, interpolated: 0, speed: 0.2 },
      loadMoreBtn: { target: 0, interpolated: 0, speed: 0.1 },
    };


  }

  ngOnInit() {
    if (this.topic) {
      this.isTopicRevision = this.topic.startsWith('revision');
    } else if (this.type) {
      this.isTypeRevision = this.type.startsWith('revision');
    }
    const subKey = this.type && this.isTypeRevision ? 'Revisions' : '';
    this.topicsPlaceholder = this.translate.instant(
      `Common.Search.${subKey}Topics.all`
    );
    this.filtersTopics = Object.entries(
      this.translate.instant(`Common.Search.${subKey}Topics`)
    );

    this.filtersTopics = this.filtersTopics
        .filter(entry => {
          return  (entry[0] !== Topic.native_communities) &&  (entry[0] !== Topic.others );
        });

    // Todo: english test only there in case of going live in urgency
    if (environment.production) {
      this.filtersTopics = this.filtersTopics
        .filter(entry => entry[0] !== Topic.english);
    }
    this.raf.bind('hub-search', this.draw.bind(this));
    window.addEventListener('resize', this.updateHeights);

    if ( this.filterKeywords ) {
      this.setKeywords(this.filterKeywords);
    }

    this.topicOrType = this.topic || this.type;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.raf.unbind('hub-search');
    window.removeEventListener('resize', this.updateHeights);
  }

  ngOnChanges(changes: SimpleChanges) {
    let resetPage = false;

    const hasTopic = changes.topic && changes.topic.currentValue;
    const hasType = changes.type && changes.type.currentValue;

    if (hasTopic) {
      this.isTopicRevision = this.topic.startsWith('revision');
      if (!this.isTopicRevision) {
        this.type = null;
        resetPage = true;
      }
    } else {
      this.isTopicRevision = false;
    }

    if (hasType && this.type) {
      this.isTypeRevision = this.type.startsWith('revision');
      if (!this.isTypeRevision) {
        this.topic = null;
        resetPage = true;
      }
    } else {
      this.isTypeRevision = false;
    }

    if (resetPage) {
      this.params.keywords = '';
      this.resetFilters();
    } else if (!this.isSearching) {
      this.search();
    }
  }

  onKeywordsKeyUp(e) {
    if (e.target.value === '') {
      this.clearSuggestions();
    }
    if (!PREVENT_KEYS[e.key]) {
      this.transitions.loading.target = 1; // show loading spinners right away
      this.transitions.loadingResults.target = 1;
      this.newSearch();
      this.getSearchSuggestions(e.target.value);

      if (e.target.value.trim() !== '') {
        this.tracking.pushVar({
          category: 'recherche',
          action: this.params.filters.type,
          label: e.target.value
        });
      }
    }
  }

  onClickClearKeywords() {
    this.clearSuggestions();
    this.setKeywords('');
  }

  onOpen(): void {
    if (this.active) {
      return;
    }
    this.active = true;

    this.transitionSearchBox();
  }

  onClose(): void {
    if (!this.active) {
      return;
    }
    this.active = false;

    this.transitionSearchBox();
  }

  toggle(): void {
    if (this.active) {
      this.onClose();
    } else {
      this.onOpen();
    }
  }

  filterOver() {
    this.filterIcon = 'hover';
  }

  filterOut() {
    this.filterIcon = 'normal';
  }

  filterChange() {
    this.filterNumber = this.getFilterCount();
    this.transitionFilters();
    this.newSearch();
  }

  getFilterCount(): number {
    let count = 0;
    for (const filter of Object.keys(this.params.filters)) {
      if (this.params.filters[filter] && (filter !== 'type' || this.topic)) {
        count++;
      }
    }
    return count;
  }

  resetFilters() {
    for (const filter of Object.keys(this.params.filters)) {
      this.params.filters[filter] = '';
    }
    this.filterNumber = 0;
    this.transitionFilters();
    setTimeout(() => this.transitionSearchBox(), 1000); // fix height after "Clear filters" button has disappeared
    this.newSearch();
  }

  setKeywords(keywords: string) {
    this.params.keywords = keywords;

    const currentUrl = this.router.url.split('?')[0] ;

    if (keywords === '') {
      this.location.go(currentUrl);
    } else {
      this.location.go(`${currentUrl}?keywords=${keywords}` );
    }
    this.newSearch();
  }

  getSearchSuggestions = debounce(() => {
    const { keywords } = this.params;
    const terms = keywords.trim();

    if (terms === '') {
      // no need for any suggestion
      return this.clearSuggestions();
    }

    this.transitions.suggestionsOpacity.target = 0;
    this.transitions.loading.target = 1;

    this.subscriptions.push(
      this.content.getSuggestions(terms).subscribe(suggestions => {
        this.searchSuggestions = suggestions.map(item => item.text);

        if (this.searchSuggestions.length) {
          this.transitionSuggestions();
          this.adaptSearchBoxForMobile();
        } else {
          this.clearSuggestions();
        }

        this.transitions.loading.target = 0;
      })
    );
  }, SEARCH_AS_YOU_TYPE_DELAY);

  clearSuggestions() {
    this.transitions.loading.target = 0;
    this.transitions.loadingResults.target = 0;
    this.transitions.suggestions.target = 0;
    this.transitions.suggestionsOpacity.target = 0;

    window.setTimeout(() => {
      this.searchSuggestions = [];
      this.transitionSuggestions();
      this.adaptSearchBoxForMobile();
    }, 500); // give some time for the animation to finish before clearing the list
  }

  search = debounce((
    from: DocumentSnapshot<any> = null,
    indexFrom: number = 0,
    isLoadMore: boolean = false
  ) => {
    const { keywords } = this.params;
    const terms = keywords.trim();

    this.isSearching = true;

    this.transitions.loadingResults.target = 1;
    this.transitions.resultsOpacity.target = 0;

    const type = this.type ? FileTypeVal[this.type]  : (this.params.filters.type as FileType);
    const topic =  (this.topic || this.params.filters.topic) as TopicVal;

    this.subscriptions.push(
      this.content
        .searchFiles(
          this.translate.currentLang,
          terms,
          type,
          topic,
          this.params.filters.level,
          this.pageSize,
          from,
          indexFrom
        )
        .subscribe(results => {

          // Firebase is used when there is no keywords, ans search gives results based on filter
          // eleastic search is used  when ther is a keyword set in search

          if (results.size > this.pageSize) {
            if (terms) {
              // LOGIC WITH ELASTIC SEARCH
              this.nextResult = results.next;
              const nextIndex = this.nextResultIndex + this.pageSize;
              this.nextResultIndex = nextIndex < results.size ? nextIndex : 0;
            } else {
               // LOGIC WITH FIREBASE
              this.nextResult = results.next;
              this.nextResultIndex = this.nextResultIndex + this.pageSize;
            }
          } else {
            this.nextResult = null;
            this.nextResultIndex = 0;
          }

          let resultsTiles = [];

          if ( type === 'file' ) {
            resultsTiles = this.checkRevisionFilesResults(results.tiles);
          } else {
            resultsTiles = results.tiles;
          }

          this.results = isLoadMore
            ? [...this.results, ...resultsTiles.slice(0, this.pageSize)]
            : resultsTiles.slice(0, this.pageSize);

          this.isSearching = false;

          this.transitions.loadingResults.target = 0;
          this.transitions.loadingMore.target = 0;
          this.transitions.loadMoreBtn.target = this.nextResult || this.nextResultIndex ? 1 : 0;

          const currentUrl = this.router.url.split('?')[0] ;

          if (keywords === '') {
            this.location.go(currentUrl);
          } else {
            this.location.go(`${currentUrl}?keywords=${terms}` );
          }

          // hack for firebase calls
          // when firebase base gives results it updte total results on every loadmore calls
          // so for ex : first call results.size = 40 , second call results.size = 28
          // so we need to save first value
          this.firstResearch  =  this.isFirstResearch ? results.size : this.firstResearch ;

          this.changeHeading(this.firstResearch);
          this.transitionResults();

          this.isFirstResearch = false;
        })
    );
  }, SEARCH_AS_YOU_TYPE_DELAY);


  checkRevisionFilesResults(tiles) {
    const filteredFiles = tiles.filter(tile => {
      return tile.subtype !== 'revision';
    });

    return filteredFiles;
  }


  newSearch() {
    this.isFirstResearch = true;
    this.firstResearch = 0;
    this.nextResult = null;
    this.nextResultIndex = 0;
    this.search();
  }

  loadMore() {
    this.transitions.loadingMore.target = 1;
    this.search(this.nextResult, this.nextResultIndex, true);
  }

  changeHeading(size: number): void {
    const { keywords, filters } = this.params;
    const { level, type } = filters;
    const hasFilters = level !== '' || type !== '';
    const shouldDisplayNumberOfResults = (keywords !== '' || hasFilters);

    const resultsLabel = size > 1
      ? this.translate.instant('Common.Search.Results.results')
      : this.translate.instant('Common.Search.Results.result');
    const heading = shouldDisplayNumberOfResults
      ? size + ' ' + resultsLabel
      : this.translate.instant('Common.Search.Results.most_popular');

    this.resultsHeading = heading;
  }

  /**
   * Animations
   */

  /**
   * TODO: enable when Popular Keywords are populated
   */

  /*
  transitionTopicTags = debounce(() => {
    this.transitions.topicTags.target = this.suggestions.length ? 0 : this.topicTagsContainer.nativeElement.offsetHeight;
  }, 100);
  */
  transitionFilters = debounce(() => {
    this.transitions.filterCount.target = this.filterNumber ? 1 : 0;
    this.transitions.resetBtn.target = this.filterNumber ? 1 : 0;
    this.transitionSearchBox();
  }, 200);
  adaptSearchBoxForMobile() {
    if (this.isMobile) {
      setTimeout(() => this.transitionSearchBox(), 500);
    }
  }
  transitionSearchBox = debounce(() => {
    this.transitions.searchOpacity.target = this.active ? 1 : 0;
    this.transitions.searchHeight.target = this.active
      ? this.searchContainer.nativeElement.clientHeight
      : 0;
  }, 100);
  transitionSuggestions = debounce(() => {
    this.transitions.suggestionsOpacity.target = this.searchSuggestions.length
      ? 1
      : 0;
    this.transitions.suggestions.target = this.searchSuggestions.length
      ? this.suggestionsContainer.nativeElement.clientHeight
      : 0;
  }, 100);
  transitionResults = debounce(() => {
    this.transitions.results.target = this.results.length
      ? this.resultsContainer.nativeElement.clientHeight
      : 0;
    this.transitions.resultsHeader.target = this.resultsHeaderContainer.nativeElement.clientHeight;
    this.transitions.resultsOpacity.target = this.results.length ? 1 : 0;
  }, 100);
  updateHeights = debounce(() => {
    this.transitions.searchOpacity.target = this.active ? 1 : 0;
    this.transitions.searchHeight.target = this.active
      ? this.searchContainer.nativeElement.clientHeight
      : 0;
    this.transitions.suggestionsOpacity.target = this.searchSuggestions.length
      ? 1
      : 0;
    this.transitions.suggestions.target = this.searchSuggestions.length
      ? this.suggestionsContainer.nativeElement.clientHeight
      : 0;
    this.transitions.results.target = this.results.length
      ? this.resultsContainer.nativeElement.clientHeight
      : 0;
    this.transitions.resultsHeader.target = this.resultsHeaderContainer.nativeElement.clientHeight;
    this.transitions.resultsOpacity.target = this.results.length ? 1 : 0;
  }, DEBOUNCE_HEIGHT_DELAY);

  resetTransitions() {
    this.transitions.loading.target = 0;
    this.transitions.suggestionsOpacity.target = 0;
    this.transitions.suggestions.target = 0;
    this.transitions.loadingResults.target = 0;
    this.transitions.results.target = 0;
    this.transitions.resultsOpacity.target = 0;
    this.transitions.resultsOpacity.target = 0;
  }

  animateInterpolated() {
    Object.keys(this.transitions).map(item => {
      const x = this.transitions[item];
      this.transitions[item].interpolated = this.tools.round(
        x.interpolated + (x.target - x.interpolated) * x.speed,
        9
      );
    });
  }

  draw() {
    this.animateInterpolated();
  }
}
